import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { Button } from "@/shared/components/atoms/Button";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { DialogConfirm } from "@/shared/components/molecules/DialogConfirm";
import { TableList } from "@/shared/components/molecules/TableList";
import {
  useCreateScaleMutation,
  useDeleteScaleMutation,
  useGetScaleQuery,
  useUpdateScaleMutation,
} from "@/app/device/device.api";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { FIELD_REQUIRED_MESSAGE } from "@/shared/utils/validations";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { toasts } from "@/shared/utils/toasts";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";

interface Device {
  id: number;
  name: string;
  secret_key: string;
  status: string;
  branch_id: number;
  created_at: string;
  updated_at: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  secret_key: Yup.string().required(FIELD_REQUIRED_MESSAGE),
});

export const DeviceLinking: FC = () => {
  const { id } = useParams();
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [openDialogEdit, setOpenDialogEdit] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  // Mock data - replace with actual API call
  const devicesQuery = useGetScaleQuery({
    branchId: id || 0,
  });
  const [updateScale, updateScaleResult] = useUpdateScaleMutation();
  const [deleteScale, deleteScaleResult] = useDeleteScaleMutation();
  const [createScale, createScaleResult] = useCreateScaleMutation();

  const createFormik = useFormik({
    initialValues: {
      name: "",
      secret_key: "",
      branch_id: Number(id) || 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await handleCreateScale(values);
        setOpenDialogCreate(false);
        createFormik.resetForm();
        devicesQuery.refetch();
      } catch (error) {
        console.error("Error", error);
      }
    },
  });

  const editFormik = useFormik({
    initialValues: {
      name: selectedDevice?.name || "",
      secret_key: selectedDevice?.secret_key || "",
      branch_id: selectedDevice?.branch_id || 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await handleUpdateScale(values);
        setOpenDialogEdit(false);
        editFormik.resetForm();
        devicesQuery.refetch();
      } catch (error) {
        console.error("Error", error);
      }
    },
    enableReinitialize: true,
  });

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setOpenDialogEdit(true);
  };

  const handleDelete = (device: Device) => {
    setSelectedDevice(device);
    setOpenDialogDelete(true);
  };

  const confirmDelete = () => {
    if (selectedDevice) {
      // Handle delete
      console.log("Delete device:", selectedDevice.id);
    }
    setOpenDialogDelete(false);
  };

  const columns = [
    { header: t("deviceLinking.views.name"), accessor: "name" },
    { header: t("deviceLinking.views.secret_key"), accessor: "secret_key" },
    { header: t("deviceLinking.views.status"), accessor: "status" },
    {
      header: t("deviceLinking.views.actions"),
      accessor: "actions",
      cell: (device: Device) => (
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => handleEdit(device)}
            text={t("general.edit")}
            primary={false}
          />

          <Trash2Icon
            onClick={() => handleDelete(device)}
            className="text-red-600"
          />
        </div>
      ),
    },
  ];

  async function handleCreateScale(values: any) {
    try {
      await createScale({ body: values, branchId: id }).unwrap();
      toasts.success(
        "Registro exitoso",
        "El dispositivo se ha creado correctamente"
      );
    } catch (error) {
      console.error("Error on handleCreateScale", error);
      toasts.danger(
        "Error al registrar",
        "Ha ocurrido un error al registrar el dispositivo"
      );
      throw error;
    }
  }

  async function handleUpdateScale(values: any) {
    try {
      await updateScale({
        ...values,
        id: selectedDevice?.id,
        branch_id: Number(id),
      }).unwrap();
      toasts.success(
        "Actualización exitosa",
        "El dispositivo se ha actualizado correctamente"
      );
    } catch (error) {
      console.error("Error on handleUpdateScale", error);
      toasts.danger(
        "Error al actualizar",
        "Ha ocurrido un error al actualizar el dispositivo"
      );
      throw error;
    }
  }

  async function handleDeleteScale(device: Device) {
    try {
      await deleteScale({ id: device.id, branch_id: id }).unwrap();
      devicesQuery.refetch();
      setOpenDialogDelete(false);
      toasts.success(
        "Eliminación exitosa",
        "El dispositivo se ha eliminado correctamente"
      );
    } catch (error) {
      console.error("Error on handleDeleteScale", error);
      toasts.danger(
        "Error al eliminar",
        "Ha ocurrido un error al eliminar el dispositivo"
      );
      throw error;
    }
  }

  return (
    <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
      <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <div className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center mb-8 border-b border-gray-300 pb-4">
            <div className="flex-grow">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                {t("deviceLinking.views.title")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("deviceLinking.views.subtitle")}
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0"></div>
            <div className="mt-8 sm:ml-auto sm:mt-4 flex items-center space-x-4">
              <div>
                <button
                  onClick={() => setOpenDialogCreate(true)}
                  type="button"
                  className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                >
                  <PlusIcon aria-hidden="true" className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <TableList
            columns={columns}
            data={devicesQuery.data?.data || []}
            isLoading={devicesQuery.isLoading}
            onClick={() => setOpenDialogCreate(true)}
          />

          <DialogCreate
            open={openDialogCreate}
            setOpen={setOpenDialogCreate}
            tittle={t("deviceLinking.views.create_device")}
            OnclickCreate={createFormik.handleSubmit}
            OnclickCancel={() => {
              setOpenDialogCreate(false);
              createFormik.resetForm();
            }}
            onClose={() => {
              setOpenDialogCreate(false);
              createFormik.resetForm();
            }}
            isLoading={createScaleResult.isLoading}
            acceptDisabled={createScaleResult.isLoading}
            tittleButtonCreate={t("general.create")}
            tittleButtonCancel={t("general.cancel")}
            dialogWidth="max-w-md"
          >
            <form onSubmit={createFormik.handleSubmit}>
              <div className="space-y-4 px-6">
                <div>
                  <InputWithCornerHint
                    id="name"
                    name="name"
                    label={t("general.name")}
                    value={createFormik.values.name}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    error={
                      createFormik.touched.name && createFormik.errors.name
                    }
                    textError={createFormik.errors.name}
                  />
                </div>
                <div>
                  <InputWithCornerHint
                    id="secret_key"
                    name="secret_key"
                    label={t("deviceLinking.views.secret_key")}
                    value={createFormik.values.secret_key}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    error={
                      createFormik.touched.secret_key &&
                      createFormik.errors.secret_key
                    }
                    textError={createFormik.errors.secret_key}
                  />
                </div>
              </div>
            </form>
          </DialogCreate>

          <DialogCreate
            open={openDialogEdit}
            setOpen={setOpenDialogEdit}
            tittle={t("deviceLinking.views.edit_device")}
            OnclickCreate={editFormik.handleSubmit}
            OnclickCancel={() => {
              setOpenDialogEdit(false);
              editFormik.resetForm();
            }}
            onClose={() => {
              setOpenDialogEdit(false);
              editFormik.resetForm();
            }}
            isLoading={updateScaleResult.isLoading}
            acceptDisabled={updateScaleResult.isLoading}
            tittleButtonCreate={t("general.save")}
            tittleButtonCancel={t("general.cancel")}
            dialogWidth="max-w-md"
          >
            <form onSubmit={editFormik.handleSubmit}>
              <div className="space-y-4 px-4">
                <div>
                  <InputWithCornerHint
                    id="name"
                    name="name"
                    label={t("general.name")}
                    value={editFormik.values.name}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                  />
                  {editFormik.touched.name && editFormik.errors.name && (
                    <p className="mt-1 text-sm text-red-500">
                      {editFormik.errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <InputWithCornerHint
                    id="secret_key"
                    name="secret_key"
                    label={t("deviceLinking.views.secret_key")}
                    value={editFormik.values.secret_key}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    error={
                      editFormik.touched.secret_key &&
                      editFormik.errors.secret_key
                    }
                    textError={editFormik.errors.secret_key}
                  />
                </div>
              </div>
            </form>
          </DialogCreate>

          <AlertErrorButtons
            open={openDialogDelete}
            onClose={() => setOpenDialogDelete(false)}
            title={t("deviceLinking.views.delete_device")}
            message={t("deviceLinking.views.delete_confirmation")}
            onPrimaryButtonClick={() => handleDeleteScale(selectedDevice!)}
            onSecondaryButtonClick={() => setOpenDialogDelete(false)}
            primaryButtonText={t("general.delete")}
            secondaryButtonText={t("general.cancel")}
            isLoading={deleteScaleResult.isLoading}
            modalClassName="z-50"
          />
        </div>
      </div>
    </div>
  );
};
